<template>
  <div class="fwd-checklist">
    <!-- Live status / test block: only for a saved account with a mailbox -->
    <div v-if="account" class="fwd-checklist__test mb_12">
      <template v-if="verifiedAt">
        <div class="fwd-checklist__proved">
          <img src="/img/renova-dashboard/icons/notification--success.svg" alt="" />
          <div>
            <p class="fwd-checklist__proved-title">
              {{ $t('Integrations.allianz.forwarding.Verified') || 'Connection proved — forwarding works' }}
            </p>
            <p class="color--grey fwd-checklist__proved-when">{{ formatWhen(verifiedAt) }}</p>
          </div>
        </div>
      </template>
      <template v-else>
        <p v-if="confirmationReceivedAt" class="fwd-checklist__pending mb_8">
          {{ $t('Integrations.allianz.forwarding.ConfirmationNote')
            || "Google's confirmation reached us — Renova is approving it; nothing is needed on your side." }}
        </p>
        <div class="fwd-checklist__test-row">
          <BaseButton
            type="outline"
            :loading="sending"
            :disabled="cooldown > 0 || !account.twoFaEmail"
            @click="sendTest"
          >
            {{ testButtonLabel }}
          </BaseButton>
          <span v-if="polling" class="fwd-checklist__waiting color--grey">
            {{ $t('Integrations.allianz.forwarding.TestWaiting') || 'Waiting for the test email to come back…' }}
          </span>
        </div>
        <p v-if="!account.twoFaEmail" class="color--grey fwd-checklist__hint">
          {{ $t('Integrations.allianz.forwarding.NeedsMailbox') || 'Save a 2FA email on the account first — the test email needs a mailbox to be sent to.' }}
        </p>
        <p v-if="testError" class="color--red fwd-checklist__hint">{{ testError }}</p>
      </template>
    </div>

    <!-- The 8-step Gmail checklist -->
    <ol class="fwd-checklist__steps">
      <li v-for="n in 8" :key="n" class="fwd-checklist__step">
        <p class="fwd-checklist__step-title">
          {{ stepText(n, 'Title') }}
        </p>
        <p class="fwd-checklist__step-desc color--grey">
          <template v-for="(part, i) in stepParts(n)">
            <span
              v-if="part.email"
              :key="`e-${n}-${i}`"
              class="fwd-checklist__email"
              role="button"
              tabindex="0"
              :title="$t('buttons.copy') || 'Copy'"
              @click="copyEmail(part.email)"
              @keydown.enter="copyEmail(part.email)"
            >{{ part.email }}</span>
            <span v-else :key="`t-${n}-${i}`">{{ part.text }}</span>
          </template>
        </p>
      </li>
    </ol>
  </div>
</template>

<script>
import axios from 'axios';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';

// A returning test email is usually back within seconds of Gmail forwarding
// it: SES -> SNS -> the 3s webhook drain. Poll fast and give up after ~3.5min.
const TEST_POLL_INTERVAL_MS = 5000;
const TEST_MAX_POLLS = 40;
// Mirrors the backend's per-account rate limit on forwarding/test.
const TEST_COOLDOWN_SEC = 60;

const STEP_FALLBACKS = {
  1: ['Add the forwarding address', 'In Gmail (on a computer): gear icon → See all settings → Forwarding and POP/IMAP → Add a forwarding address. Paste the Renova address shown above. Google emails a confirmation — Renova approves it, no code will be shown to you.'],
  2: ['Enable forwarding', 'After Renova approves the confirmation, make sure forwarding is actually on: either "Forward a copy of incoming mail" on the same tab, or a filter whose action is "Forward it to" the Renova address.'],
  3: ['Check the filter condition', "If you forward with a filter, its From condition must cover both senders — type secure.token@allianz.com.mx OR no-reply@renova.world into the From field (OR is Gmail's operator). A filter limited to Allianz senders will not forward the Renova test email below."],
  4: ['Add a temporary test label', 'Optional: add "Apply label: FORWARD_TEST" to the same filter. If the label appears on a test email but nothing reaches Renova, the filter works and the problem is the forwarding step itself.'],
  5: ['Send the test', 'Use the "Send test email" button above — or send any email to this mailbox from a third address. Do not send it from the mailbox that receives the forwarded mail: Gmail refuses to forward back to the sender.'],
  6: ['Check the original email headers', 'Open the test email → More (⋮) → Show original, and confirm To / Delivered-To name the expected address.'],
  7: ['Check Spam', 'The original message must actually arrive in this mailbox. If it lands in Spam it is not forwarded — add "Never send it to Spam" to the filter if needed.'],
  8: ['Google Workspace restrictions', 'On a company (Workspace) mailbox the administrator may have disabled automatic forwarding to external addresses. If the filter works but nothing is forwarded, check Google Admin Console → Gmail settings.'],
};

export default {
  name: 'AllianzForwardingChecklist',
  components: { BaseButton },
  props: {
    /** A saved scrape account (enables the live test). Null while creating. */
    account: { type: Object, default: null },
    receiverAddress: { type: String, required: true },
  },
  emits: ['updated'],
  data() {
    return {
      sending: false,
      testError: '',
      polling: false,
      poll: null,
      pollCount: 0,
      cooldown: 0,
      cooldownTimer: null,
      // Fresher copy of the account row, updated by the verification poll.
      liveAccount: null,
    };
  },
  computed: {
    current() {
      return this.liveAccount || this.account;
    },
    verifiedAt() {
      return this.current && this.current.forwardingVerifiedAt;
    },
    confirmationReceivedAt() {
      return this.current && this.current.forwardingConfirmationReceivedAt;
    },
    testButtonLabel() {
      if (this.cooldown > 0) {
        return `${this.$t('Integrations.allianz.forwarding.TestSent') || 'Test sent'} (${this.cooldown})`;
      }
      return this.$t('Integrations.allianz.forwarding.SendTest') || 'Send test email';
    },
  },
  beforeDestroy() {
    this.stopPolling();
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
  },
  methods: {
    stepText(n, part) {
      const key = `Integrations.allianz.forwarding.Step${n}${part}`;
      const translated = this.$t(key);
      const fallback = STEP_FALLBACKS[n][part === 'Title' ? 0 : 1];
      const text = translated && translated !== key ? translated : fallback;
      return text.replace('{address}', this.receiverAddress);
    },
    /**
     * Split a step description into plain text and email addresses, so every
     * address renders as a highlighted click-to-copy chip — they are the part
     * of the instructions people actually have to carry into Gmail.
     */
    stepParts(n) {
      const text = this.stepText(n, 'Desc');
      const re = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
      const parts = [];
      let last = 0;
      let m = re.exec(text);
      while (m) {
        if (m.index > last) parts.push({ text: text.slice(last, m.index) });
        parts.push({ email: m[0] });
        last = m.index + m[0].length;
        m = re.exec(text);
      }
      if (last < text.length) parts.push({ text: text.slice(last) });
      return parts;
    },
    copyEmail(email) {
      navigator.clipboard.writeText(email)
        .then(() => this.$notify.success(this.$t('adminTitles.CopiedToClipboard')));
    },
    formatWhen(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
    },
    sendTest() {
      if (!this.account) return;
      this.sending = true;
      this.testError = '';
      axios
        .post('insurance/policy/scrape/account/forwarding/test', {
          scrapeAccountId: this.account.scrapeAccountId,
        })
        .then(() => {
          this.startCooldown();
          this.startPolling();
        })
        .catch((err) => {
          this.testError = (err && err.response && err.response.data && err.response.data.message)
            || (err && err.message)
            || this.$t('adminTitles.someError');
        })
        .finally(() => {
          this.sending = false;
        });
    },
    startCooldown() {
      this.cooldown = TEST_COOLDOWN_SEC;
      if (this.cooldownTimer) clearInterval(this.cooldownTimer);
      this.cooldownTimer = setInterval(() => {
        this.cooldown -= 1;
        if (this.cooldown <= 0) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
      }, 1000);
    },
    startPolling() {
      if (this.poll) return; // idempotent: never stack intervals
      this.polling = true;
      this.pollCount = 0;
      this.poll = setInterval(() => {
        this.pollCount += 1;
        if (this.pollCount > TEST_MAX_POLLS) {
          this.stopPolling();
          return;
        }
        axios
          .post('insurance/policy/scrape/account/list', {})
          .then((res) => {
            const items = (res && res.data && res.data.items) || [];
            const mine = items.find((a) => a.scrapeAccountId === this.account.scrapeAccountId);
            if (!mine) return;
            this.liveAccount = mine;
            if (mine.forwardingVerifiedAt) {
              this.stopPolling();
              this.$emit('updated');
            }
          })
          .catch(() => { /* transient: keep polling until the cap */ });
      }, TEST_POLL_INTERVAL_MS);
    },
    stopPolling() {
      this.polling = false;
      if (this.poll) {
        clearInterval(this.poll);
        this.poll = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.fwd-checklist {
  &__test {
    padding: 12px;
    background: var(--solid-grey-light);
    border-radius: 8px;
  }

  &__test-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__waiting {
    font-size: 13px;
  }

  &__hint {
    font-size: 12px;
    margin-top: 6px;
  }

  &__pending {
    font-size: 13px;
    color: #f0a020;
  }

  &__proved {
    display: flex;
    align-items: center;
    gap: 12px;

    img {
      width: 40px;
      height: 40px;
    }
  }

  &__proved-title {
    font-weight: 600;
    color: #18a058;
  }

  &__proved-when {
    font-size: 12px;
  }

  &__steps {
    margin: 0;
    padding: 0;
    list-style: none;
    counter-reset: fwd-step;
  }

  &__step {
    position: relative;
    padding: 0 0 14px 36px;
    counter-increment: fwd-step;

    // Number circle + connector line, the OnboardingSteps look on a light bg.
    &::before {
      content: counter(fwd-step);
      position: absolute;
      left: 0;
      top: 0;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 1px solid #d5d5e0;
      color: #513eff;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff;
    }

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      left: 12px;
      top: 26px;
      bottom: 2px;
      width: 1px;
      background: #e6e6ee;
    }
  }

  &__step-title {
    font-weight: 600;
    font-size: 13px;
    line-height: 24px;
  }

  &__step-desc {
    font-size: 12px;
    line-height: 17px;
    margin-top: 2px;
  }

  &__email {
    display: inline;
    padding: 1px 6px;
    border-radius: 4px;
    background: #ece9ff;
    color: #513eff;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
  }
}
</style>
